"use client";

/**
 * Tiny shared utility for the crash log buffer used by both <CrashCapture/>
 * (window-level handlers) and Next.js error boundaries (React render
 * errors). Keeping it in one place means the buffer schema and storage
 * key stay in lockstep regardless of which surface caught the error.
 *
 * Persistence model: an in-memory array is the session source of truth. We
 * hydrate it from localStorage once, append in memory, and persist on a
 * debounce. This matters because CrashCapture logs `visibilitychange` and
 * `pageshow` on *every* tab switch — the previous "read-modify-write
 * localStorage on every push" did a synchronous JSON.parse + JSON.stringify
 * right on the focus transition, adding to tab-back jank. Terminal events
 * (error/rejection/boundary/pagehide) still flush synchronously since the
 * page may be about to die.
 */

export const CRASH_LOG_STORAGE_KEY = "__crashLog";
export const CRASH_LOG_MAX_EVENTS = 50;

export type CrashLogEvent =
  | { t: number; type: "error"; message: string; source?: string; line?: number; col?: number; stack?: string }
  | { t: number; type: "rejection"; reason: string; stack?: string }
  | { t: number; type: "boundary"; message: string; digest?: string; stack?: string }
  | { t: number; type: "visibilitychange"; hidden: boolean }
  | { t: number; type: "pagehide"; persisted: boolean }
  | { t: number; type: "pageshow"; persisted: boolean };

// --- raw storage accessors (no buffering) ---

function readStored(): CrashLogEvent[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(CRASH_LOG_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    // Shape check, not just a parse guard: any valid non-array JSON under this
    // key ({}, "x", 0 — a stale key, a half-written value) became the buffer,
    // and `events.push` then threw inside error.tsx's RENDER body, taking the
    // crash screen down with it.
    return Array.isArray(parsed) ? (parsed as CrashLogEvent[]) : [];
  } catch {
    return [];
  }
}

export function writeCrashLog(events: CrashLogEvent[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CRASH_LOG_STORAGE_KEY,
      JSON.stringify(events.slice(-CRASH_LOG_MAX_EVENTS)),
    );
  } catch {
    // Quota exceeded or storage disabled — best effort, swallow.
  }
}

// --- in-memory buffer + debounced flush ---

let buffer: CrashLogEvent[] | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function ensureBuffer(): CrashLogEvent[] {
  if (buffer === null) buffer = readStored();
  return buffer;
}

function flushNow() {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (buffer !== null) writeCrashLog(buffer);
}

function scheduleFlush() {
  if (typeof window === "undefined" || flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    if (buffer !== null) writeCrashLog(buffer);
  }, 1000);
}

/** Current rolling buffer — in-memory if hydrated, else read from storage. */
export function readCrashLog(): CrashLogEvent[] {
  return buffer !== null ? buffer.slice() : readStored();
}

/** Wipe both the in-memory buffer and the persisted copy. */
export function clearCrashLog() {
  buffer = [];
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(CRASH_LOG_STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

export function pushCrashLog(event: CrashLogEvent) {
  const events = ensureBuffer();
  events.push(event);
  if (events.length > CRASH_LOG_MAX_EVENTS) {
    events.splice(0, events.length - CRASH_LOG_MAX_EVENTS);
  }
  // High-frequency lifecycle events buffer + debounce; terminal events
  // (we might be about to die) persist synchronously.
  if (event.type === "visibilitychange" || event.type === "pageshow") {
    scheduleFlush();
  } else {
    flushNow();
  }
  // Terminal events only. The lifecycle events above fire on every tab switch,
  // so an unconditional warn filled the production console with
  // `[crash-capture] {type:'visibilitychange'}` lines — burying the one entry
  // anybody opens the console for, and retaining every logged object.
  if (event.type === "error" || event.type === "rejection" || event.type === "boundary") {
     
    console.warn("[crash-capture]", event);
  }
}
